import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { 
  FaBuilding, 
  FaGraduationCap, 
  FaBook, 
  FaCalendar, 
  FaUser, 
  FaDoorOpen,
  FaClipboardCheck,
  FaCode,
  FaSave,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import {
  useColleges,
  useDegrees,
  usePrograms,
  useAcademicYears,
  useCourses,
  useRoomMaster,
  useRegistrationTypes,
  useExamAnnouncements
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ExamAttendance = () => {
  const [formData, setFormData] = useState({
    academicYear: '',
    programmes: '',
    colleges: '',
    degree: '',
    courses: '',
    examAnnouncement: '',
    exStudentIdFrom: '',
    exStudentIdTo: '',
    roomId: '',
    registrationType: '',
    exCoursesCode: '',
    user: 1
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmes: programs, loading: programsLoading } = usePrograms();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { courses, loading: coursesLoading } = useCourses();
  const { rooms, loading: roomsLoading } = useRoomMaster();
  const { registrationTypes, loading: registrationTypesLoading } = useRegistrationTypes();
  const { examAnnouncements, loading: examAnnouncementsLoading } = useExamAnnouncements();

  const dropdownsLoading = collegesLoading || degreesLoading || programsLoading || 
                          academicYearsLoading || coursesLoading || roomsLoading || 
                          registrationTypesLoading || examAnnouncementsLoading;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'roomId' && value) {
      const selectedRoom = rooms.find(r => r.id === Number(value));
      if (selectedRoom) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          exRoomName: selectedRoom.exRoomName || selectedRoom.roomName || selectedRoom.exName || selectedRoom.name || '',
          exRoomNumber: selectedRoom.exRoomNumber || selectedRoom.roomNumber || selectedRoom.id?.toString() || ''
        }));
      }
    }
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.academicYear) newErrors.academicYear = 'Academic Year is required';
    if (!formData.programmes) newErrors.programmes = 'Programme is required';
    if (!formData.colleges) newErrors.colleges = 'College is required';
    if (!formData.degree) newErrors.degree = 'Degree is required';
    if (!formData.courses) newErrors.courses = 'Course is required';
    if (!formData.examAnnouncement) newErrors.examAnnouncement = 'Exam Announcement is required';
    if (!formData.exStudentIdFrom) newErrors.exStudentIdFrom = 'Student ID From is required';
    if (!formData.exStudentIdTo) newErrors.exStudentIdTo = 'Student ID To is required';
    if (!formData.roomId) newErrors.roomId = 'Room is required';
    if (!formData.registrationType) newErrors.registrationType = 'Registration Type is required';
    if (!formData.exCoursesCode) newErrors.exCoursesCode = 'Course Code is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = Cookies.get('token');
      
      const payload = {
        academicYear: Number(formData.academicYear),
        programmes: Number(formData.programmes),
        colleges: Number(formData.colleges),
        degree: Number(formData.degree),
        courses: Number(formData.courses),
        examAnnouncement: Number(formData.examAnnouncement),
        exStudentIdFrom: formData.exStudentIdFrom.trim(),
        exStudentIdTo: formData.exStudentIdTo.trim(),
        roomId: Number(formData.roomId),
        registrationType: Number(formData.registrationType),
        user: 1,
        exCoursesCode: formData.exCoursesCode.trim(),
        exRoomName: formData.exRoomName || '',
        exRoomNumber: formData.exRoomNumber || ''
      };

      console.log('Submitting payload:', payload);

      const response = await axios.post(
        `${BASE_URL}/academic/createExamAttendance`,
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Response:', response.data);
      toast.success('Exam attendance created successfully!');
      handleReset();

    } catch (error) {
      console.error('Failed to create exam attendance:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to create exam attendance';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      academicYear: '',
      programmes: '',
      colleges: '',
      degree: '',
      courses: '',
      examAnnouncement: '',
      exStudentIdFrom: '',
      exStudentIdTo: '',
      roomId: '',
      registrationType: '',
      exCoursesCode: '',
      user: 1
    });
    setErrors({});
  };

  if (dropdownsLoading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading form data...</span>
          </div>
          <p className="mt-3 text-muted">Loading dropdown data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <FaClipboardCheck className="me-2 text-primary" />
                Exam Attendance
              </h4>
              <p className="text-muted small mb-0">Academic / Exam Attendance</p>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <div className="bg-light border rounded p-4 mb-4">
            <h5 className="mb-3">
              <FaInfoCircle className="me-2 text-primary" />
              Create Exam Attendance
            </h5>

            <Form onSubmit={handleSubmit}>
              <div className="mb-4">
                <h6 className="text-primary mb-3 border-bottom pb-2">
                  <FaGraduationCap className="me-2" />
                  Academic Information
                </h6>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaBuilding className="me-2 text-muted" />
                        College <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="colleges"
                        value={formData.colleges}
                        onChange={handleChange}
                        isInvalid={!!errors.colleges}
                      >
                        <option value="">Select College</option>
                        {(Array.isArray(colleges) ? colleges : [])
                          .filter(Boolean)
                          .map(c => (
                            <option key={c.id} value={c.id}>
                              {c.exName}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.colleges}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaGraduationCap className="me-2 text-muted" />
                        Degree <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="degree"
                        value={formData.degree}
                        onChange={handleChange}
                        isInvalid={!!errors.degree}
                      >
                        <option value="">Select Degree</option>
                        {(Array.isArray(degrees) ? degrees : [])
                          .filter(Boolean)
                          .map(d => (
                            <option key={d.id} value={d.id}>
                              {d.exName}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.degree}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaBook className="me-2 text-muted" />
                        Programme <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="programmes"
                        value={formData.programmes}
                        onChange={handleChange}
                        isInvalid={!!errors.programmes}
                      >
                        <option value="">Select Programme</option>
                        {(Array.isArray(programs) ? programs : [])
                          .filter(Boolean)
                          .map(p => (
                            <option key={p.id} value={p.id}>
                              {p.exName}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.programmes}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaCalendar className="me-2 text-muted" />
                        Academic Year <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="academicYear"
                        value={formData.academicYear}
                        onChange={handleChange}
                        isInvalid={!!errors.academicYear}
                      >
                        <option value="">Select Academic Year</option>
                        {(Array.isArray(academicYears) ? academicYears : [])
                          .filter(Boolean)
                          .map(a => (
                            <option key={a.id} value={a.id}>
                              {a.exYear}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.academicYear}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="text-primary mb-3 border-bottom pb-2">
                  <FaBook className="me-2" />
                  Course & Exam Details
                </h6>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaBook className="me-2 text-muted" />
                        Course <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="courses"
                        value={formData.courses}
                        onChange={handleChange}
                        isInvalid={!!errors.courses}
                      >
                        <option value="">Select Course</option>
                        {(Array.isArray(courses) ? courses : [])
                          .filter(Boolean)
                          .map(c => (
                            <option key={c.id} value={c.id}>
                              {c.exName || c.name}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.courses}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaCode className="me-2 text-muted" />
                        Course Code <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="exCoursesCode"
                        value={formData.exCoursesCode}
                        onChange={handleChange}
                        placeholder="e.g., CS101"
                        isInvalid={!!errors.exCoursesCode}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.exCoursesCode}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaClipboardCheck className="me-2 text-muted" />
                        Exam Announcement <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="examAnnouncement"
                        value={formData.examAnnouncement}
                        onChange={handleChange}
                        isInvalid={!!errors.examAnnouncement}
                      >
                        <option value="">Select Exam Announcement</option>
                        {(Array.isArray(examAnnouncements) ? examAnnouncements : [])
                          .filter(Boolean)
                          .map(ea => (
                            <option key={ea.id} value={ea.id}>
                              {ea.exName || ea.name || `Exam ${ea.id}`}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.examAnnouncement}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaUser className="me-2 text-muted" />
                        Registration Type <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="registrationType"
                        value={formData.registrationType}
                        onChange={handleChange}
                        isInvalid={!!errors.registrationType}
                      >
                        <option value="">Select Registration Type</option>
                        {(Array.isArray(registrationTypes) ? registrationTypes : [])
                          .filter(Boolean)
                          .map(rt => (
                            <option key={rt.id} value={rt.id}>
                              {rt.exName || rt.name}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.registrationType}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="text-primary mb-3 border-bottom pb-2">
                  <FaUser className="me-2" />
                  Student & Room Details
                </h6>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaUser className="me-2 text-muted" />
                        Student ID From <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="exStudentIdFrom"
                        value={formData.exStudentIdFrom}
                        onChange={handleChange}
                        placeholder="e.g., CS24 0001"
                        isInvalid={!!errors.exStudentIdFrom}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.exStudentIdFrom}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaUser className="me-2 text-muted" />
                        Student ID To <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="exStudentIdTo"
                        value={formData.exStudentIdTo}
                        onChange={handleChange}
                        placeholder="e.g., CS24 0001"
                        isInvalid={!!errors.exStudentIdTo}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.exStudentIdTo}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <FaDoorOpen className="me-2 text-muted" />
                        Room <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        name="roomId"
                        value={formData.roomId}
                        onChange={handleChange}
                        isInvalid={!!errors.roomId}
                      >
                        <option value="">Select Room</option>
                        {(Array.isArray(rooms) ? rooms : [])
                          .filter(Boolean)
                          .map(r => (
                            <option key={r.id} value={r.id}>
                              {r.exRoomName || r.roomName || r.exName || r.name || `Room ${r.id}`}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.roomId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  onClick={handleReset}
                  disabled={isSubmitting}
                >
                  <FaTimes className="me-2" />
                  Cancel
                </Button>
                <Button
                  variant="warning"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Add
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </div>

          <Alert variant="info" className="mb-0">
            <FaInfoCircle className="me-2" />
            <strong>Note:</strong> All fields marked with <span className="text-danger">*</span> are required. 
            Make sure to fill in the student ID range and select the appropriate room for the exam.
          </Alert>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ExamAttendance;